import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../modules/auth/store'
import { NotificationBell } from '../../modules/notifications/components'
import { Menu, X, Home, PawPrint, Users, User, LogOut, ClipboardList } from 'lucide-react'
import logoSvg from '../../assets/logo.svg'

interface LayoutProps {
  children: React.ReactNode
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/')

  const closeMobileMenu = () => setMobileMenuOpen(false)

  const isAdmin = user?.role === 'admin'

  const navLinks = [
    { path: '/', label: 'Inicio', icon: Home, exact: true },
    { path: '/mascotas', label: 'Mascotas', icon: PawPrint },
    { path: '/grupo', label: 'Grupo', icon: Users },
    ...(isAdmin ? [{ path: '/bitacora', label: 'Bitácora', icon: ClipboardList }] : []),
    { path: '/perfil', label: 'Perfil', icon: User },
  ]

  return (
    <div className="layout">
      <header className="header">
        <div className="header-content">
          <Link to="/" className="logo">
            <img src={logoSvg} alt="" className="logo-icon" />
            Rondela
          </Link>
          {user && (
            <>
              <nav className="nav">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={
                      link.exact
                        ? location.pathname === link.path
                          ? 'active'
                          : ''
                        : isActive(link.path)
                        ? 'active'
                        : ''
                    }
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
              <div className="user-menu">
                <span className="user-name">{user.nombre}</span>
                <NotificationBell />
                <button onClick={logout} className="btn btn-ghost btn-sm" aria-label="Cerrar sesión">
                  <LogOut size={16} />
                  <span className="sr-only">Salir</span>
                </button>
              </div>
              <button
                className="menu-toggle"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Abrir menú"
                aria-expanded={mobileMenuOpen}
              >
                <Menu size={24} />
              </button>
            </>
          )}
        </div>
      </header>

      {/* Mobile navigation */}
      {user && (
        <nav className={`nav-mobile ${mobileMenuOpen ? 'open' : ''}`} aria-label="Navegación móvil">
          <div className="nav-mobile-header">
            <span className="logo">
              <img src={logoSvg} alt="" className="logo-icon" />
              Rondela
            </span>
            <button className="btn btn-ghost" onClick={closeMobileMenu} aria-label="Cerrar menú">
              <X size={24} />
            </button>
          </div>
          <div className="nav-mobile-links">
            {navLinks.map((link) => {
              const Icon = link.icon
              const active = link.exact
                ? location.pathname === link.path
                : isActive(link.path)
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={active ? 'active' : ''}
                  onClick={closeMobileMenu}
                >
                  <Icon size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                  {link.label}
                </Link>
              )
            })}
            <button
              onClick={() => {
                closeMobileMenu()
                logout()
              }}
              className="btn btn-ghost"
              style={{ justifyContent: 'flex-start', width: '100%', padding: '1rem' }}
            >
              <LogOut size={20} style={{ marginRight: '0.5rem' }} />
              Cerrar sesión
            </button>
          </div>
        </nav>
      )}

      <main className="main">{children}</main>
    </div>
  )
}
